// ---------------------------------------------------------------------------
// BK-016 — Office Display Pre-Shared Keys
// ---------------------------------------------------------------------------
//
// Schema half of the BK-016 unblock specified in
// `docs/vision_execution/openclaw/BK-016-DECISION-PACKET.md` §"What is missing
// for BK-016" point 2 ("Pre-shared tenant display key") and §"V1 decision —
// BLOCKED on tenant-display-key contract".
//
// A wall-mounted TV or kiosk cannot present an operator login flow, so the
// existing tenant-auth `/displays/leaderboard` endpoint is not reachable from
// a lobby device. This table holds the per-display pre-shared keys (hashed
// only — raw token never persisted) that an admin issues once at the
// "Add a screen" moment, with the upcoming public `workers/page-serve/
// serve-office-display.ts` route resolving them at request time.
//
// Token-handling pattern lifted from `signature_requests.tokenHash` in
// `packages/db/src/schema/proposals.ts` (SC-1 capability-token contract):
//   1. Generate cryptographically random raw token (32+ bytes).
//   2. Display raw token ONCE in the admin UI for the operator to paste into
//      the screen's bookmark / kiosk URL.
//   3. Store only `HMAC-SHA256(rawToken, OFFICE_DISPLAY_TOKEN_HASH_SECRET)`
//      so a DB leak does not yield a working public URL.
//   4. Lookup on incoming request: HMAC the candidate token with the same
//      secret, query by `token_hash`. Constant-time compare on the row.
//
// Re-open / public route work tracked in the decision packet — this PR ships
// schema + privacy filter only.

import {
  pgTable,
  text,
  timestamp,
  uuid,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export const officeDisplayKeys = pgTable("office_display_keys", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  /**
   * Links the screen to the configured display (`office_displays.id` —
   * defined in `conversation-flows.ts`). The relationship is intentionally
   * NOT a FK constraint at the DB layer; tenant scoping is enforced
   * application-side per the platform's "no RLS" rule, and not every
   * future office_displays migration target is in the same Postgres
   * schema (schema-per-tenant tier).
   */
  officeDisplayId: uuid("office_display_id").notNull(),
  /**
   * HMAC-SHA256 hash of the raw capability token. Raw token is shown
   * ONCE in the admin UI at issuance and never re-readable from the DB.
   * Unique across all tenants — collision astronomically improbable but
   * the index protects against accidental duplicate inserts.
   */
  tokenHash: text("token_hash").notNull().unique(),
  /**
   * Operator-facing display name for the screen ("Front desk TV",
   * "Waiting room iPad"). Never carries PII — clamped at the route
   * layer by the privacy filter.
   */
  label: text("label").notNull(),
  /**
   * `auth.user.id` of the admin who issued the key. Tracked for audit
   * + future revoke-on-admin-offboard sweeps. Nullable in case the
   * issuer is later deleted (we keep the key valid; revocation is an
   * explicit action).
   */
  createdByUserId: uuid("created_by_user_id"),
  /**
   * Last-seen heartbeat from the public route (updated by
   * `serve-office-display.ts` at most once per heartbeat window).
   * Surfaces dead screens in the admin list view.
   */
  lastSeenAt: timestamp("last_seen_at", tz),
  /**
   * Soft-revoke marker. Once set, the route refuses to resolve the
   * key. We keep the row for audit and to prevent a re-issued raw
   * token from colliding with a historic hash.
   */
  revokedAt: timestamp("revoked_at", tz),
  /**
   * Optional expiry. NULL = never expires (admin-controlled). When
   * set, the route refuses to resolve after this timestamp.
   */
  expiresAt: timestamp("expires_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_office_display_keys_tenant").on(table.tenantId),
  index("idx_office_display_keys_display").on(table.tenantId, table.officeDisplayId),
  index("idx_office_display_keys_revoked").on(table.tenantId, table.revokedAt),
]);

export type OfficeDisplayKey = typeof officeDisplayKeys.$inferSelect;
export type NewOfficeDisplayKey = typeof officeDisplayKeys.$inferInsert;
